<script lang="ts" module>
  import type { ReviewDraft as Draft } from '../api/server';
  import type { PrStore as Store } from '../state/pr.svelte';
  import { parseOffDiff } from '../logic/offdiff';
  import { carryFilter } from '../logic/filefilter';
  import { THREAD_PARAM } from '../logic/headerTargets';
  import { prPath } from '../router';
  import { nav } from '../nav.svelte';

  /** Where a draft opens in Files: its line (at the snapshot it was left on), or its thread for a reply. */
  export function draftHref(pr: Store, d: Draft): string {
    const t = d.target;
    // One on the whole pull request opens where it was written: the Overview.
    if (t.kind === 'pr') return carryFilter(prPath(pr.owner, pr.repo, pr.number), nav.query);
    const q = new URLSearchParams({ file: t.path });
    if (t.kind === 'reply') q.set(THREAD_PARAM, t.thread_id);
    else {
      const m = t.kind === 'file' ? parseOffDiff(d.body)?.marker : null;
      const line = t.kind === 'file' ? (m?.side === 'RIGHT' ? m.line : null) : t.side === 'RIGHT' ? t.line : null;
      if (line) q.set('line', String(line));
      const commit = t.kind === 'file' ? (m?.commit ?? t.commit_sha) : t.commit_sha;
      if (pr.pr && commit !== pr.pr.headRefOid && pr.snapshots.some((s) => s.sha === commit)) q.set('to', commit);
    }
    return carryFilter(prPath(pr.owner, pr.repo, pr.number, 'files') + '?' + q.toString(), nav.query);
  }
</script>

<script lang="ts">
  // Managing a list of drafts in place, by keyboard and by each draft's own
  // controls: the Review view's "Unsent" part. A draft is ticked (included
  // in the review being prepared) or it is not; nothing here sends anything.
  //
  // Commands (commands/drafts.ts; their keys are the `unsent` section of
  // the bindings table): the draft motions move the cursor; including
  // ticks the draft at it and moves on to the next (on a ticked one,
  // unticks it and stays); editing, discarding and opening act on it. The
  // cursor's draft has the focus, and the verbs act only while the focus
  // is in the list: from anywhere else on the page their keys are left to
  // the page, so no draft is ticked or discarded unseen. The motions work
  // from anywhere: the first one enters the list at the cursor.
  import { tick, untrack } from 'svelte';
  import type { DiscardedOnGitHub, ReviewDraft } from '../api/server';
  import { subject, type PrStore } from '../state/pr.svelte';
  import { afterTick, isIncluded, listOrder, mirroredComment, MIRRORED_DISCARD, type Fate } from '../logic/inbox';
  import { useKeys, viewModel } from '../keys/keyboard.svelte';
  import { updateDrafts, type DraftsEffect } from '../commands/drafts';
  import type { Command } from '../commands/command';
  import { toast } from '../state/toast.svelte';
  import DraftList from './DraftList.svelte';

  interface Props {
    pr: PrStore;
    /** The drafts to list (any order: the list orders them by file and line). */
    drafts: readonly ReviewDraft[];
    /** What becomes of each draft at the next publish, where the page knows (the Review view). */
    fate?: ((id: ReviewDraft['id']) => Fate) | null;
    /** Why nothing in the list can be changed now (it is out of date, or being published), in words; null when it can. */
    locked?: string | null;
    /** The drafts a publish is sending now: each says so in place. */
    sending?: ReadonlySet<ReviewDraft['id']> | null;
    /** Told each change made here to draft `id`, with the write that makes it (settled when it is in, or rolled back). */
    onchange?: (id: ReviewDraft['id'], write: Promise<unknown>) => void;
  }
  let { pr, drafts, fate = null, locked = null, sending = null, onchange }: Props = $props();

  const list = $derived(listOrder(drafts));
  // The cursor is the list's model, held by a Program: commands move it, and so do a click on a draft and
  // the list changing under it (`rest`).
  const model = viewModel({
    init: { cursor: null as string | null },
    env: () => ({ drafts: list.map((d) => String(d.id)), inList: !!root?.contains(document.activeElement) }),
    update: updateDrafts,
    perform: (e: DraftsEffect) => perform(e),
    announce: (text) => say(text),
  });
  const draftOf = (id: string | null) => (id === null ? undefined : list.find((d) => String(d.id) === id));
  const cursor = $derived(draftOf(model.state.cursor)?.id ?? null);
  const rest = (id: ReviewDraft['id'] | null) => void model.send({ kind: 'rest', id: id === null ? null : String(id) });
  let editing: ReviewDraft['id'] | null = $state(null);

  // The cursor stays on a draft that is listed: the first, or the one now at its place.
  let lastIndex = 0;
  $effect(() => {
    const ids = list.map((d) => d.id);
    const at = cursor === null ? -1 : ids.indexOf(cursor);
    if (at >= 0) lastIndex = at;
    else untrack(() => rest(ids.length ? ids[Math.min(lastIndex, ids.length - 1)] : null));
  });

  /** Put the cursor on draft `id`, in view and focused: focus says which draft it is on (the card's label). */
  async function goTo(id: ReviewDraft['id']) {
    rest(id);
    await tick();
    const el = document.querySelector<HTMLElement>(`[data-draft="${CSS.escape(String(id))}"]`);
    // The scroll is ours, so it can be smooth.
    el?.focus({ preventScroll: true });
    el?.scrollIntoView?.({ block: 'nearest', behavior: typeof matchMedia === 'function' && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
  }

  let root: HTMLElement | undefined = $state();
  /** Whether a key pressed on `target` was pressed in the list: only then does it act on a draft. */
  const inList = (target: Element | null) => !!target && !!root?.contains(target);

  /** Said to a screen reader for a key that changes nothing, or whose effect is not where the focus is. */
  let said = $state<{ id: number; text: string } | null>(null);
  const say = (text: string) => (said = { id: (said?.id ?? 0) + 1, text });
  /** Why nothing here can be changed now, said when a key or a control is tried. */
  const refused = () => (locked !== null ? (say(locked), true) : pr.draftsOff);

  /**
   * Include `d` in the review being prepared, or take an included one out:
   * the include command. With `advance` (the keys) a tick moves the cursor
   * on to the next draft, and the cursor's draft takes the focus and is
   * scrolled into view. Without it (the draft's own control) the cursor
   * rests on `d`, and neither the focus nor the scroll moves.
   */
  function toggle(d: ReviewDraft, advance: boolean) {
    if (refused()) return;
    const on = !isIncluded(d);
    onchange?.(d.id, pr.includeDraft(d.id, on));
    const next = on && advance ? afterTick(list, d.id) : d.id;
    if (advance) void goTo(next);
    else rest(d.id);
    say(on ? (advance && next === d.id ? 'Included. That was the last draft.' : 'Included.') : 'Not included.');
  }

  /**
   * Discard, with Undo in the notice (it is held again, as it was). A draft
   * whose comment is in the viewer's pending review on GitHub takes that
   * comment with it, so that is asked first; the server deletes it there
   * only while GitHub shows it still pending, and the notice says what it
   * found (a comment published since is left alone, and has no Undo).
   */
  async function discard(d: ReviewDraft) {
    if (refused() || pr.writes.on(subject.draft(d.id))) return;
    const onGitHub = mirroredComment(d) !== null;
    if (onGitHub && !confirm(MIRRORED_DISCARD)) return;
    if (editing === d.id) editing = null;
    let found: DiscardedOnGitHub = null;
    // The toast says it (with Undo), so the write itself stays quiet.
    const gone = pr.discardDraft(d.id, `held:${d.id}`, { quiet: true, onGitHub: (f) => (found = f) });
    onchange?.(d.id, gone);
    if (!(await gone)) return;
    // The focus was on the draft that went: it goes to the one now at the cursor, so the keys still have a draft.
    await tick();
    if (cursor !== null && (!document.activeElement || document.activeElement === document.body)) void goTo(cursor);
    // Published on GitHub since it was last read: nothing was deleted there, and there is no draft to bring back.
    if (found === 'published') return toast.show({ text: 'That comment is already published on GitHub, so nothing was deleted there. Its draft is removed.' });
    toast.show({
      text: found === 'deleted' ? 'Draft discarded, and its comment deleted from your pending review on GitHub' : 'Draft discarded',
      action: {
        label: 'Undo',
        run: () => {
          const back = pr.restoreDraft(d);
          onchange?.(d.id, back);
        },
      },
    });
  }

  function edit(d: ReviewDraft) {
    if (refused() || pr.writes.on(subject.draft(d.id))) return;
    editing = d.id;
  }

  const hrefOf = (d: ReviewDraft) => draftHref(pr, d);

  const jump = (d: ReviewDraft) => nav.go(hrefOf(d));
  /** A command on draft `d` itself, from one of its own controls: the same command its key sends for the cursor's draft. */
  const act = (verb: 'include' | 'open' | 'discard', d: ReviewDraft) => void send({ kind: 'act', verb, at: { object: 'draft', id: String(d.id) } });

  // The list's commands; everything else is the Review view's and the page's.
  useKeys({ scope: 'unsent', handle: (command) => send(command) });

  /** Sends a command, from a key or from a draft's own controls. */
  const send = (command: Command) => model.send(command);

  function perform(e: DraftsEffect) {
    const d = draftOf(e.id);
    if (!d) return;
    switch (e.kind) {
      case 'show':
        return goTo(d.id);
      case 'toggle':
        return toggle(d, e.advance);
      case 'edit':
        return edit(d);
      case 'discard':
        return discard(d);
      case 'jump':
        return jump(d);
    }
  }
</script>

<div class="draft-triage" bind:this={root}>
<DraftList
  {pr}
  drafts={list}
  {cursor}
  {fate}
  {locked}
  {sending}
  bind:editing
  onselect={(d) => rest(d.id)}
  {hrefOf}
  onjump={(d) => act('open', d)}
  ondiscard={(d) => act('discard', d)}
  oninclude={(d) => act('include', d)}
  onedit={(d, write) => onchange?.(d.id, write)}
/>
</div>

<div class="sr-only" aria-live="polite">{#key said?.id}{said?.text ?? ''}{/key}</div>
